<template>
  <header ref="headerRef" class="site-header sticky top-0 z-[1002] bg-white shadow-[0_1px_0_#e9ecef]">

    <!-- ① Optionele banner-afbeelding -->
    <div v-if="bannerImg" class="w-full overflow-hidden">
      <img :src="bannerImg" :alt="bannerAlt" class="w-full h-auto object-cover max-h-[80px]">
    </div>
    <!-- Lege slot voor banner (verwijder de v-if hierboven en zet bannerImg om deze te activeren) -->

    <!-- ② Topbar -->
    <div class="bg-[#111] text-white text-[0.8rem] py-[0.45rem]">
      <div class="max-w-screen-xl mx-auto px-4 flex items-center justify-between gap-4">
        <!-- Roterende USP links -->
        <transition name="topbar-fade" mode="out-in">
          <span :key="topbarIndex" class="flex items-center gap-1.5 text-white/80">
            <i :class="['bi', topbarMessages[topbarIndex].icon, 'text-white/60']"></i>
            {{ topbarMessages[topbarIndex].text }}
          </span>
        </transition>
        <router-link to="/klantenservice" class="flex items-center gap-1.5 no-underline shrink-0">
          <span class="flex text-white/70 text-[0.7rem]" aria-hidden="true">
            <i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-fill"></i><i class="bi bi-star-half"></i>
          </span>
          <span class="text-white/80 font-semibold">4,9 uit 380+ reviews</span>
        </router-link>
      </div>
    </div>

    <!-- ② Main header -->
    <div class="bg-white py-3 border-b border-[#e9ecef]">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="flex items-center gap-4">

          <!-- Hamburger (mobiel) -->
          <button
            type="button"
            :aria-label="showMobileMenu ? 'Menu sluiten' : 'Menu openen'"
            class="md:hidden bg-transparent border-none text-[1.375rem] text-[#333] cursor-pointer p-1 -ml-1"
            @click="showMobileMenu = !showMobileMenu"
          >
            <i class="bi" :class="showMobileMenu ? 'bi-x' : 'bi-list'"></i>
          </button>

          <!-- Logo -->
          <router-link to="/" class="shrink-0 flex items-center no-underline" aria-label="Plug2Charge.nl - home">
            <Logo :height="40" />
          </router-link>

          <!-- Zoekbalk -->
          <div class="flex-1 max-w-[600px] mx-auto hidden md:flex border-[1.5px] border-[#e9ecef] rounded-[6px] bg-[#fafafa] focus-within:border-[#FFD500] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,213,0,0.20)] relative">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              @keyup.enter="handleSearch"
              @input="onSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keydown.down.prevent="moveSuggestion(1)"
              @keydown.up.prevent="moveSuggestion(-1)"
              @keydown.escape="closeSuggestions"
              type="text"
              autocomplete="off"
              class="flex-1 px-4 py-2.5 border-none outline-none bg-transparent text-[0.875rem] placeholder:text-[#aaa]"
              placeholder="Waar ben je naar op zoek?"
            >
            <!-- Zoeksuggesties -->
            <transition name="fade-slide">
              <div
                v-if="showSuggestions && (suggestions.length > 0 || suggestionsLoading)"
                class="absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[#e9ecef] rounded-[10px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[300] overflow-hidden"
              >
                <div v-if="suggestionsLoading && suggestions.length === 0" class="px-4 py-3 text-[0.8rem] text-[#888]">
                  <i class="bi bi-arrow-repeat spin text-[#f0b800]"></i> Zoeken…
                </div>
                <template v-else>
                  <router-link
                    v-for="(item, i) in suggestions"
                    :key="item.id"
                    :to="`/product/${item.slug}`"
                    @click="closeSuggestions"
                    class="flex items-center gap-3 px-4 py-2.5 no-underline transition-colors border-b border-[#f5f5f5] last:border-b-0"
                    :class="{ 'bg-[#fffbe6]': i === activeSuggestion }"
                    style="color: #111"
                  >
                    <div class="w-10 h-10 shrink-0 rounded-[5px] bg-[#f8f9fa] border border-[#f0f0f0] flex items-center justify-center">
                      <img v-if="item.image" :src="item.image" :alt="item.name" loading="lazy" class="w-full h-full object-contain">
                      <i v-else class="bi bi-image text-[#ccc] text-sm"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-[0.85rem] font-medium truncate">{{ item.name }}</div>
                      <div class="text-[0.75rem] text-[#888]">{{ item.category }}</div>
                    </div>
                    <div class="shrink-0 text-right">
                      <div class="text-[0.875rem] font-bold">{{ formatPrice(item.price) }}</div>
                    </div>
                  </router-link>
                  <button @click="handleSearch" class="w-full px-4 py-2.5 bg-[#fffbe6] text-[0.82rem] font-semibold text-[#333] text-left hover:bg-[#fff3b0] flex items-center gap-2 border-none cursor-pointer">
                    <i class="bi bi-search text-[#b8860b]"></i>
                    Alle resultaten voor "{{ searchQuery }}"
                  </button>
                </template>
              </div>
            </transition>
            <button
              type="button"
              aria-label="Zoeken"
              @click="handleSearch"
              class="bg-[#FFD500] border-none px-5 text-[#111] cursor-pointer text-base hover:bg-[#f0b800] transition-colors rounded-r-[4px]"
            >
              <i class="bi bi-search"></i>
            </button>
          </div>

          <!-- Utility links (rechts) -->
          <div class="flex items-center gap-1 ml-auto md:ml-0">
            <router-link to="/account" class="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#555] no-underline text-[0.8125rem] hover:text-[#111] transition-colors rounded-[6px] hover:bg-[#f5f5f5]">
              <i class="bi bi-person"></i>
              <span>Mijn account</span>
            </router-link>
            <router-link to="/klantenservice" class="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[#555] no-underline text-[0.8125rem] hover:text-[#111] transition-colors rounded-[6px] hover:bg-[#f5f5f5]">
              <i class="bi bi-headset"></i>
              <span>Klantenservice</span>
            </router-link>
            <router-link to="/cart" aria-label="Winkelwagen" class="relative w-10 h-10 flex items-center justify-center bg-[#FFD500] text-[#111] no-underline rounded-[6px] text-lg hover:bg-[#f0b800] ml-1 header-cart-btn">
              <i class="bi bi-cart"></i>
              <span v-if="cartItemCount > 0" :class="['absolute top-px right-px bg-red-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[0.625rem] font-bold border-2 border-white cart-badge', cartBadgeBump ? 'cart-badge--bump' : '']">{{ cartItemCount }}</span>
            </router-link>
          </div>

        </div>
      </div>
    </div>

    <!-- ③ Nav bar -->
    <div class="nav-bar hidden md:block bg-white border-b border-[#e9ecef]" @mouseleave="closeAllMenus">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="flex items-center justify-between">
          <nav class="flex items-stretch gap-0">

            <router-link to="/categorie/laadpalen" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Laadpalen
            </router-link>

            <router-link to="/categorie/laadkabels" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Laadkabels
            </router-link>

            <!-- Thuisbatterijen dropdown -->
            <div class="flex items-stretch relative" @mouseenter="openMenu('batterijen')">
              <div
                class="nav-link px-4 h-11 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer"
                :class="{ active: activeMenu === 'batterijen' }"
                @click="toggleMenu('batterijen')"
              >
                Thuisbatterijen
                <i class="bi bi-chevron-down text-[0.6rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'batterijen' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'batterijen'" class="simple-dropdown">
                  <router-link to="/categorie/thuisbatterijen" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-battery-full text-[#FFD500]"></i> Thuisbatterijen
                  </router-link>
                  <router-link to="/categorie/plug-in-batterijen" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-plug text-[#FFD500]"></i> Plug-in batterijen
                  </router-link>
                  <router-link to="/categorie/all-in-one" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-box-seam text-[#FFD500]"></i> All-in-one systemen
                  </router-link>
                  <router-link to="/categorie/complete-pakketten" class="dropdown-item" @click="closeAllMenus">
                    <i class="bi bi-grid text-[#FFD500]"></i> Complete pakketten
                  </router-link>
                </div>
              </transition>
            </div>

            <router-link to="/categorie/zonnepanelen" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Zonnepanelen
            </router-link>

            <router-link to="/categorie/omvormers" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Omvormers
            </router-link>

            <router-link to="/merken" class="nav-link px-4 h-11 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center">
              Merken
            </router-link>

            <router-link to="/acties" class="nav-link px-4 h-11 text-[#c0392b] no-underline font-semibold text-[0.875rem] whitespace-nowrap inline-flex items-center gap-1.5">
              <i class="bi bi-tag-fill text-[0.8rem]"></i> Aanbiedingen
            </router-link>

          </nav>

          <!-- Keuzehulp CTA -->
          <router-link
            to="/keuzehulp"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-[6px] no-underline font-semibold text-[0.8125rem] bg-[#FFD500] text-[#111] hover:bg-[#f0b800] transition-colors shrink-0 whitespace-nowrap"
          >
            <i class="bi bi-ui-checks-grid"></i>
            Keuzehulp
          </router-link>
        </div>
      </div>
    </div>

    <!-- ④ Mobiel menu -->
    <transition name="mob-menu">
      <div
        v-if="showMobileMenu"
        class="md:hidden fixed left-0 right-0 bottom-0 bg-white z-[1001] overflow-y-auto"
        :style="{ top: headerHeight + 'px' }"
      >
        <!-- Zoekbalk -->
        <div class="px-4 pt-4 pb-3 border-b border-[#f0f0f0]">
          <div class="flex gap-2">
            <input
              v-model="searchQuery"
              @keyup.enter="handleSearch(); showMobileMenu=false"
              type="text"
              placeholder="Zoek producten..."
              class="mob-search-input flex-1 px-3.5 py-2.5 border border-[#e5e7eb] rounded-[8px] outline-none text-[0.875rem] bg-[#f8f9fa]"
            >
            <button
              @click="handleSearch(); showMobileMenu=false"
              class="mob-search-btn bg-[#FFD500] border-none px-4 rounded-[8px] text-[#111] cursor-pointer text-base"
            >
              <i class="bi bi-search"></i>
            </button>
          </div>
        </div>

        <div class="px-4 py-3 flex flex-col gap-0.5">

          <!-- Categorieën -->
          <p class="mob-section-label">Categorieën</p>

          <router-link to="/categorie/laadpalen"       class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-plug-fill"></i></span>
            <span>Laadpalen</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/categorie/laadkabels"      class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-lightning-charge-fill"></i></span>
            <span>Laadkabels</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/categorie/thuisbatterijen" class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-battery-full"></i></span>
            <span>Thuisbatterijen</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/categorie/zonnepanelen"    class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-brightness-high-fill"></i></span>
            <span>Zonnepanelen</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/categorie/omvormers"       class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-cpu-fill"></i></span>
            <span>Omvormers</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/merken"                    class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon"><i class="bi bi-award-fill"></i></span>
            <span>Merken</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>

          <!-- Keuzehulp CTA -->
          <router-link to="/keuzehulp" class="mob-cta" @click="showMobileMenu=false">
            <i class="bi bi-ui-checks-grid"></i>
            Keuzehulp — welke lader past bij mij?
            <i class="bi bi-arrow-right ml-auto"></i>
          </router-link>

          <!-- Divider -->
          <div class="mob-divider"></div>

          <!-- Aanbiedingen -->
          <router-link to="/acties" class="mob-item mob-item--sale" @click="showMobileMenu=false">
            <span class="mob-icon mob-icon--sale"><i class="bi bi-tag-fill"></i></span>
            <span>Aanbiedingen</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>

          <!-- Utility -->
          <p class="mob-section-label mt-1">Mijn Plug2Charge</p>

          <router-link to="/account"       class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon mob-icon--muted"><i class="bi bi-person"></i></span>
            <span>Mijn account</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>
          <router-link to="/klantenservice" class="mob-item" @click="showMobileMenu=false">
            <span class="mob-icon mob-icon--muted"><i class="bi bi-headset"></i></span>
            <span>Klantenservice</span>
            <i class="bi bi-chevron-right mob-arrow"></i>
          </router-link>

        </div>
      </div>
    </transition>

    <!-- Overlay -->
    <transition name="fade">
      <div
        v-if="showMobileMenu"
        class="fixed inset-0 bg-black/40 z-[1000] md:hidden"
        :style="{ top: headerHeight + 'px' }"
        @click="showMobileMenu=false"
      ></div>
    </transition>

  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import Logo from '@components/Logo.vue'
import { useAnalytics } from '@composables/useAnalytics'
import { formatPrice } from '@/utils/price'

const router    = useRouter()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const { trackSearch } = useAnalytics()

const searchQuery    = ref('')
const showMobileMenu = ref(false)
const activeMenu     = ref(null)
const searchInputRef = ref(null)
const headerRef      = ref(null)
const headerHeight   = ref(108) // fallback

const updateHeaderHeight = () => {
  if (headerRef.value) headerHeight.value = headerRef.value.offsetHeight
}

// Banner-afbeelding boven de header (optioneel)
// Zet hier een URL om een promobanner te tonen, bijv.:
// const bannerImg = '/wp-content/uploads/2026/04/banner-zomer.jpg'
const bannerImg  = null
const bannerAlt  = 'Promotie banner'

// Zoeksuggesties
const suggestions        = ref([])
const suggestionsLoading = ref(false)
const showSuggestions    = ref(false)
const activeSuggestion   = ref(-1)
let   suggestDebounce    = null

const fetchSuggestions = async (q) => {
  if (q.length < 2) { suggestions.value = []; suggestionsLoading.value = false; return }
  suggestionsLoading.value = true
  try {
    const res = await fetch(`/_search?q=${encodeURIComponent(q)}`)
    if (res.ok) suggestions.value = await res.json()
  } catch { suggestions.value = [] }
  finally { suggestionsLoading.value = false }
}

const onSearchInput = () => {
  activeSuggestion.value = -1
  const q = searchQuery.value.trim()
  showSuggestions.value = q.length >= 2
  clearTimeout(suggestDebounce)
  if (q.length >= 2) {
    suggestionsLoading.value = true
    suggestDebounce = setTimeout(() => fetchSuggestions(q), 220)
  } else {
    suggestions.value = []
    suggestionsLoading.value = false
  }
}

const onSearchFocus = () => { if (searchQuery.value.trim().length >= 2) showSuggestions.value = true }
const onSearchBlur  = () => { setTimeout(() => { showSuggestions.value = false; activeSuggestion.value = -1 }, 200) }
const closeSuggestions = () => { showSuggestions.value = false; activeSuggestion.value = -1; suggestions.value = [] }
const moveSuggestion = (dir) => {
  if (!showSuggestions.value || !suggestions.value.length) return
  activeSuggestion.value = Math.max(-1, Math.min(suggestions.value.length - 1, activeSuggestion.value + dir))
}

const cartItemCount = computed(() => cartStore.itemCount)

// ── Roterende topbar USPs ─────────────────────────────────────────────────────
const topbarMessages = [
  { icon: 'bi-truck',          text: 'Gratis verzending v.a. €50' },
  { icon: 'bi-lightning-charge', text: 'Bestel voor 17:00 → morgen in huis' },
  { icon: 'bi-credit-card',    text: 'Achteraf betalen beschikbaar' },
  { icon: 'bi-shield-check',   text: '2 jaar garantie op alle producten' },
  { icon: 'bi-arrow-clockwise', text: '30 dagen retourrecht' },
]
const topbarIndex = ref(0)
let topbarTimer = null
const cartBadgeBump = ref(false)
watch(cartItemCount, (newVal, oldVal) => {
  if (newVal > oldVal) {
    cartBadgeBump.value = true
    setTimeout(() => { cartBadgeBump.value = false }, 400)
  }
})

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    trackSearch(searchQuery.value.trim())
    router.push({ path: '/producten', query: { search: searchQuery.value.trim() } })
    searchQuery.value = ''
    closeSuggestions()
    showMobileMenu.value = false
  }
}

const openMenu     = (name) => { activeMenu.value = name }
const toggleMenu   = (name) => { activeMenu.value = activeMenu.value === name ? null : name }
const closeAllMenus = () => { activeMenu.value = null }

const lockScroll = () => {
  if (typeof window === 'undefined') return
  const y = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.top = `-${y}px`
  document.body.style.width = '100%'
}
const unlockScroll = () => {
  if (typeof window === 'undefined') return
  const y = document.body.style.top
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  window.scrollTo(0, parseInt(y || '0') * -1)
}

watch(showMobileMenu, (v) => { v ? lockScroll() : unlockScroll() })

onMounted(() => {
  wishlistStore.initWishlist()
  updateHeaderHeight()
  window.addEventListener('resize', updateHeaderHeight)
  topbarTimer = setInterval(() => {
    topbarIndex.value = (topbarIndex.value + 1) % topbarMessages.length
  }, 3500)
})
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    unlockScroll()
    window.removeEventListener('resize', updateHeaderHeight)
  }
  clearInterval(topbarTimer)
})
</script>

<style scoped>
.nav-link {
  border-bottom: 3px solid transparent;
  transition: color 0.18s, border-color 0.18s, background 0.18s;
  box-sizing: border-box;
}
.nav-link:hover,
.nav-link.router-link-active,
.nav-link.router-link-exact-active,
.nav-link.active {
  color: #111;
  border-bottom-color: #FFD500;
}

.nav-bar { position: relative; }

/* Simple dropdown */
.simple-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.10);
  min-width: 220px;
  padding: 6px;
  z-index: 200;
}
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border-radius: 6px;
  text-decoration: none;
  color: #333;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s;
}
.dropdown-item:hover { background: #fffbe6; }

/* ── Mobiel menu ─────────────────────────────────────────────────────────── */
.mob-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #aaa;
  margin: 0.75rem 0 0.35rem 0.25rem;
}

.mob-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.625rem;
  border-radius: 9px;
  text-decoration: none;
  color: #222;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: background 130ms cubic-bezier(0.23, 1, 0.32, 1),
              color     130ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-item:hover,
.mob-item.router-link-active { background: #fffbe6; color: #111; }
.mob-item.router-link-active .mob-icon { background: #FFD500; color: #111; }

.mob-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: background 130ms cubic-bezier(0.23, 1, 0.32, 1),
              color     130ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-item:hover .mob-icon { background: #FFD500; color: #111; }
.mob-icon--sale { background: #fef2f2; color: #c0392b; }
.mob-item:hover .mob-icon--sale { background: #c0392b; color: #fff; }
.mob-icon--muted { background: #f3f4f6; color: #888; }

.mob-item--sale { color: #c0392b; }

.mob-arrow {
  margin-left: auto;
  color: #ccc;
  font-size: 0.7rem;
  flex-shrink: 0;
}

/* Keuzehulp CTA */
.mob-cta {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 1rem;
  background: #FFD500;
  border-radius: 10px;
  text-decoration: none;
  color: #111;
  font-size: 0.875rem;
  font-weight: 700;
  margin: 0.5rem 0;
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform  150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-cta:hover { background: #f0b800; }
.mob-cta:active { transform: scale(0.98); }

.mob-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 0.5rem 0;
}

/* Search inside menu */
.mob-search-input {
  transition: border-color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-search-input:focus { border-color: #FFD500; background: #fff; }

.mob-search-btn {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform  150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-search-btn:active { transform: scale(0.97); }

/* Cart + nav CTA: press feedback */
.header-cart-btn {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform  150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.header-cart-btn:active { transform: scale(0.95); }

/* Topbar roterende USP fade */
.topbar-fade-enter-active { transition: opacity 300ms, transform 300ms cubic-bezier(0.23,1,0.32,1); }
.topbar-fade-leave-active  { transition: opacity 200ms, transform 200ms cubic-bezier(0.77,0,0.175,1); }
.topbar-fade-enter-from    { opacity: 0; transform: translateY(6px); }
.topbar-fade-leave-to      { opacity: 0; transform: translateY(-6px); }
@media (prefers-reduced-motion: reduce) {
  .topbar-fade-enter-active, .topbar-fade-leave-active { transition: opacity 150ms; }
  .topbar-fade-enter-from, .topbar-fade-leave-to { transform: none; }
}

/* Cart badge bump — speelt als item wordt toegevoegd */
@keyframes cart-badge-bump {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.45); }
  70%  { transform: scale(0.9); }
  100% { transform: scale(1); }
}
.cart-badge { transform-origin: center; }
.cart-badge--bump { animation: cart-badge-bump 380ms cubic-bezier(0.23, 1, 0.32, 1) forwards; }
@media (prefers-reduced-motion: reduce) { .cart-badge--bump { animation: none; } }

.mob-item:active { transform: scale(0.98); }

/* Transitions */
.menu-drop-enter-active { transition: opacity 150ms cubic-bezier(0.23, 1, 0.32, 1), transform 150ms cubic-bezier(0.23, 1, 0.32, 1); }
.menu-drop-leave-active { transition: opacity 100ms cubic-bezier(0.23, 1, 0.32, 1); }
.menu-drop-enter-from, .menu-drop-leave-to { opacity: 0; transform: translateY(-6px); }

.fade-slide-enter-active { transition: opacity 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 180ms cubic-bezier(0.23, 1, 0.32, 1); }
.fade-slide-leave-active { transition: opacity 120ms cubic-bezier(0.23, 1, 0.32, 1); }
.fade-slide-enter-from { opacity: 0; transform: translateY(-4px); }
.fade-slide-leave-to { opacity: 0; }

.mob-menu-enter-active {
  transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-menu-leave-active {
  transition: opacity 160ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.mob-menu-enter-from, .mob-menu-leave-to { opacity: 0; transform: translateY(-8px); }

.fade-enter-active, .fade-leave-active { transition: opacity 220ms cubic-bezier(0.23, 1, 0.32, 1); }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }

button:focus-visible, a:focus-visible, input:focus-visible {
  outline: 2px solid #FFD500;
  outline-offset: 2px;
}
</style>
